import { useMutation, useQuery } from '@tanstack/react-query';
import { wahaClient } from '@/services/waha-client';
import { WAContact } from '@/services/interface';

/**
 * Hook for WhatsApp contacts operations
 */
export function useWhatsappContacts(sessionName: string = 'default') {
  // Check if a phone number exists on WhatsApp
  const checkNumberStatusMutation = useMutation<
    { chatId?: string; numberExists: boolean },
    Error,
    { phone: string }
  >({
    mutationFn: async ({ phone }) => {
      // Use GET /api/contacts/check-exists instead of the deprecated endpoint
      return await wahaClient.get<{ chatId?: string; numberExists: boolean }>(
        `/api/contacts/check-exists?phone=${encodeURIComponent(phone)}&session=${sessionName}`,
      );
    },
  });

  // Get all contacts
  const useContacts = () => {
    return useQuery({
      queryKey: ['contacts', sessionName],
      queryFn: async () => {
        return await wahaClient.getSessionResource<WAContact[]>(
          sessionName,
          '/contacts',
        );
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get contact detail
  const useContactDetail = (contactId: string) => {
    return useQuery({
      queryKey: ['contact', sessionName, contactId],
      queryFn: async () => {
        return await wahaClient.getSessionResource<WAContact>(
          sessionName,
          `/contacts/${contactId}`,
        );
      },
      enabled: !!contactId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Block contact
  const blockContactMutation = useMutation<void, Error, { contactId: string }>({
    mutationFn: async ({ contactId }) => {
      await wahaClient.postSessionResource(
        sessionName,
        `/contacts/${contactId}/block`,
        {},
      );
    },
  });

  // Unblock contact
  const unblockContactMutation = useMutation<
    void,
    Error,
    { contactId: string }
  >({
    mutationFn: async ({ contactId }) => {
      await wahaClient.postSessionResource(
        sessionName,
        `/contacts/${contactId}/unblock`,
        {},
      );
    },
  });

  return {
    checkNumberStatus: checkNumberStatusMutation.mutateAsync,
    isCheckingNumber: checkNumberStatusMutation.isPending,
    useContacts,
    useContactDetail,
    blockContact: blockContactMutation.mutateAsync,
    unblockContact: unblockContactMutation.mutateAsync,
    isUpdatingContact:
      blockContactMutation.isPending || unblockContactMutation.isPending,
  };
}
